/**
 * Google Analytics 4 (GA4) Event Tracking Helpers
 * 
 * This module provides type-safe wrappers for tracking custom events in GA4.
 * All events are sent client-side via gtag.js.
 */

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'consent',
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

/**
 * Check if GA4 is loaded and available
 */
export function isGAAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Track a page view
 * GA4 automatically tracks page views, but this allows for custom metadata
 */
export function trackPageView(url: string, title: string) {
  if (!isGAAvailable()) return;

  window.gtag!('event', 'page_view', {
    page_location: url,
    page_title: title,
  });
}

/**
 * Track coin/token page view with metadata
 * This helps understand which coins are most popular
 */
export function trackCoinView(params: {
  symbol: string;
  name: string;
  price?: number;
  marketCap?: number;
  change24h?: number;
}) {
  if (!isGAAvailable()) return;

  window.gtag!('event', 'view_coin', {
    coin_symbol: params.symbol,
    coin_name: params.name,
    coin_price: params.price,
    coin_market_cap: params.marketCap,
    coin_change_24h: params.change24h,
    event_category: 'crypto',
    event_label: `${params.symbol} - ${params.name}`,
  });
}

/**
 * Track NFT collection page view with metadata
 * This helps understand which NFT collections are trending
 */
export function trackNFTView(params: {
  collectionName: string;
  collectionSlug: string;
  floorPrice?: number;
  volume24h?: number;
  totalSupply?: number;
}) {
  if (!isGAAvailable()) return;

  window.gtag!('event', 'view_nft_collection', {
    collection_name: params.collectionName,
    collection_slug: params.collectionSlug,
    floor_price: params.floorPrice,
    volume_24h: params.volume24h,
    total_supply: params.totalSupply,
    event_category: 'nft',
    event_label: params.collectionName,
  });
}

/**
 * Track article/news page view with metadata
 * This helps understand which content topics resonate most
 */
export function trackArticleView(params: {
  title: string;
  slug: string;
  category?: string;
  tags?: string[];
  contentType?: string;
  author?: string;
}) {
  if (!isGAAvailable()) return;

  window.gtag!('event', 'view_article', {
    article_title: params.title,
    article_slug: params.slug,
    article_category: params.category,
    article_tags: params.tags?.join(', '),
    content_type: params.contentType || 'news',
    article_author: params.author,
    event_category: 'content',
    event_label: params.title,
  });
}

/**
 * Track search queries
 * Useful for understanding what users are looking for
 */
export function trackSearch(params: {
  query: string;
  resultsCount: number;
  searchType?: 'coins' | 'nfts' | 'articles' | 'all';
}) {
  if (!isGAAvailable()) return;

  window.gtag!('event', 'search', {
    search_term: params.query,
    search_results_count: params.resultsCount,
    search_type: params.searchType || 'all',
    event_category: 'engagement',
  });
}

/**
 * Track social share actions
 * Helps measure content virality
 */
export function trackShare(params: {
  platform: 'twitter' | 'copy_link' | 'other';
  contentType: 'article' | 'coin' | 'nft';
  contentTitle: string;
  contentUrl: string;
}) {
  if (!isGAAvailable()) return;

  window.gtag!('event', 'share', {
    method: params.platform,
    content_type: params.contentType,
    item_id: params.contentUrl,
    event_category: 'engagement',
    event_label: `${params.platform} - ${params.contentTitle}`,
  });
}

/**
 * Track external link clicks (e.g., to trading platforms)
 */
export function trackExternalLinkClick(params: {
  url: string;
  linkText: string;
  location: string; // Where on the page the link was clicked
}) {
  if (!isGAAvailable()) return;

  window.gtag!('event', 'click', {
    link_url: params.url,
    link_text: params.linkText,
    link_location: params.location,
    event_category: 'engagement',
    event_label: `External: ${params.linkText}`,
  });
}

/**
 * Track ad spot impressions and clicks
 * Complements the existing ad analytics service
 */
export function trackAdEngagement(params: {
  adSpotId: string;
  action: 'view' | 'click';
  location: string;
}) {
  if (!isGAAvailable()) return;

  window.gtag!('event', params.action === 'click' ? 'ad_click' : 'ad_impression', {
    ad_spot_id: params.adSpotId,
    ad_location: params.location,
    event_category: 'advertising',
  });
}

/**
 * Track user engagement events
 */
export function trackEngagement(params: {
  action: string;
  category: string;
  label?: string;
  value?: number;
}) {
  if (!isGAAvailable()) return;

  window.gtag!('event', params.action, {
    event_category: params.category,
    event_label: params.label,
    value: params.value,
  });
}

/**
 * Track social and custom link clicks from token/collection pages
 * Helps understand which external links drive the most engagement
 */
export function trackSocialLinkClick(params: {
  linkType: 'website' | 'twitter' | 'telegram' | 'discord' | 'custom';
  linkLabel?: string; // For custom links
  assetType: 'token' | 'collection';
  assetName: string;
  assetSlug: string;
  destination: string;
}) {
  if (!isGAAvailable()) return;

  window.gtag!('event', 'social_link_click', {
    link_type: params.linkType,
    link_label: params.linkLabel || params.linkType,
    asset_type: params.assetType,
    asset_name: params.assetName,
    asset_slug: params.assetSlug,
    link_destination: params.destination,
    event_category: 'engagement',
    event_label: `${params.assetType}:${params.linkType} - ${params.assetName}`,
  });
}


